import { toast } from 'helpers/toast';
import { fetchGalleryAttachments, searchGalleryAttachment } from 'services/gallery/gallery.service';
import { RootState } from 'store/rootReducers';
import { call, put, select } from 'typed-redux-saga';
import * as actions from './gallery.action';
import * as types from './gallery.actionTypes';
import { GalleryGroupByType, GalleryState, GalleryTabEnum } from './type.d';
import { AttachmentGalleryQueryOptions, AttachmentGalleryResponse } from '@nimbly-technologies/nimbly-common';
import { delay, race, take, takeLatest } from 'redux-saga/effects';
import {
	FETCH_FEATURE_ACCESS_FAILURE,
	FETCH_FEATURE_ACCESS_SUCCESS,
} from 'reducers/featureAccess/featureAccess.actionTypes';
import { initialSearchValues } from './gallery.reducer';
import { cloneDeep } from 'lodash';
import moment from 'moment';

export const FEATURE_ACCESS_WAIT_MS = 10000;

/**
 * Hold a gallery request until feature access has loaded.
 *
 * The service picks the v1 or v2 endpoint from ATTACHMENT_GALLERY_V2 at request time, and the
 * gallery routes do not gate on a feature, so a cold load of a gallery URL mounts the page and
 * fires the first fetch before the flags arrive. That request went to v1, came back empty, and the
 * grid showed "No Data Found" for a range that had photos until the user pressed Apply again -
 * which is why shared `?startDate=&endDate=` links looked broken.
 *
 * A failed or never-sent flag request must not hang the page, hence the error check and timeout.
 */
export function* waitForFeatureAccess() {
	const { isLoaded, error } = yield* select((state: RootState) => state.featureAccess);
	if (isLoaded || error) return;

	yield race({
		loaded: take([FETCH_FEATURE_ACCESS_SUCCESS, FETCH_FEATURE_ACCESS_FAILURE]),
		timeout: delay(FEATURE_ACCESS_WAIT_MS),
	});
}

function getQueryObject(
	{ limit, groupBy, ...state }: GalleryState,
	queryObject?: Partial<AttachmentGalleryQueryOptions>,
	loadMore?: boolean,
): Partial<AttachmentGalleryQueryOptions> {
	const isSection = groupBy !== GalleryGroupByType.ALL;

	// The reducer no longer advances `page` on request, so the page being asked
	// for is derived here and confirmed by the API response. A request cancelled
	// by takeLatest therefore leaves `state.page` untouched and the same page is
	// retried instead of skipped.
	const page = loadMore ? state.page + 1 : 1;
	const query: Partial<AttachmentGalleryQueryOptions> = { limit, page, groupBy, ...queryObject };

	// Backend doesn't support search param when groupBy is 'all'
	if (state.searchQuery && isSection) {
		query.search = state.searchQuery;
	}

	for (const [key, value] of Object.entries(state.filters)) {
		// Booleans are handled separately because they have no `.length`. The original check
		// silently dropped every boolean filter, which is why `isFailedAttachment` never reached
		// the API despite being in the filter state and read by the backend usecase.
		if (typeof value === 'boolean') {
			if (value) {
				query[key as keyof AttachmentGalleryQueryOptions] = value as never;
			}
			continue;
		}
		if (value?.length) {
			query[key as keyof AttachmentGalleryQueryOptions] = value;
		}
	}
	return query;
}

/**
 * Append a page onto an already-loaded list, dropping anything already present.
 *
 * A retried page (see the takeLatest note in getQueryObject) can legitimately
 * return rows the client already holds. Spreading into a `new Set` did not
 * deduplicate these — the entries are distinct object references — so repeats
 * rendered as duplicate thumbnails.
 */
function mergeAttachments(
	existing: AttachmentGalleryResponse[],
	incoming: AttachmentGalleryResponse[],
): AttachmentGalleryResponse[] {
	const seen = new Set(existing.map((attachment) => attachment.id));
	return [...existing, ...incoming.filter((attachment) => !seen.has(attachment.id))];
}

function getResult(
	results: AttachmentGalleryResponse[] | Record<string, AttachmentGalleryResponse[]>,
	state: GalleryState,
	loadMore?: boolean,
) {
	const isSection = state.groupBy !== GalleryGroupByType.ALL;
	const attachments: AttachmentGalleryResponse[] = Array.isArray(results) ? results : [];
	const sections = isSection ? (results as Record<string, AttachmentGalleryResponse[]>) : {};

	if (!loadMore) {
		return { attachments, sections };
	}

	if (isSection) {
		const updatedSections = cloneDeep(state.sections);
		Object.entries(sections).forEach(([key, section]) => {
			updatedSections[key] = mergeAttachments(updatedSections[key] || [], section);
		});

		return { attachments: [], sections: updatedSections };
	}

	return { attachments: mergeAttachments(state.attachments, attachments), sections: {} };
}

export function* fetchAttachmentsSaga({ payload }: ReturnType<typeof actions.fetchAttachments.request>) {
	yield* waitForFeatureAccess();

	const galleryState = (state: RootState) => state.gallery;
	const state: GalleryState = yield select(galleryState);
	const hasDateFilter = Boolean(state.filters.startDate || state.filters.endDate);

	try {
		const queryObject = getQueryObject(state, undefined, payload?.loadMore);
		if (
			state.activeTab === GalleryTabEnum.SEARCH &&
			state.searchQuery &&
			!hasDateFilter &&
			!queryObject.startDate &&
			!queryObject.endDate
		) {
			queryObject.startDate = moment().subtract(7, 'days').format('YYYY-MM-DD') as unknown as Date;
			queryObject.endDate = moment().format('YYYY-MM-DD') as unknown as Date;
		}
		const { results, ...rest } = yield* call(fetchGalleryAttachments, queryObject);
		const result = getResult(results, state, payload?.loadMore);
		const returnPayload = { ...rest, ...result };

		yield put(actions.fetchAttachments.success(returnPayload));
	} catch (e) {
		toast.error((e as Error).message);
		yield put(actions.fetchAttachments.failure({ error: 'Failed to fetch attachments' }));
	}
}

export function* searchAttachmentsSaga() {
	yield* waitForFeatureAccess();

	const galleryState = (state: RootState) => state.gallery;
	const state: GalleryState = yield select(galleryState);
	const hasDateFilter = Boolean(state.filters.startDate || state.filters.endDate);

	if (!state.searchQuery) {
		yield put(actions.searchAttachments.success(initialSearchValues));
		return;
	}

	try {
		const queryObject = getQueryObject(state, {
			limit: undefined,
			page: undefined,
		});
		// Ensure the search term is always sent when performing a search, even when groupBy is "all"
		queryObject.search = state.searchQuery;

		if (!hasDateFilter && !queryObject.startDate && !queryObject.endDate) {
			queryObject.startDate = moment().subtract(7, 'days').format('YYYY-MM-DD') as unknown as Date;
			queryObject.endDate = moment().format('YYYY-MM-DD') as unknown as Date;
		}

		const result = yield* call(searchGalleryAttachment, queryObject);

		yield put(actions.searchAttachments.success(result));
	} catch (e) {
		toast.error((e as Error).message);
		yield put(actions.searchAttachments.failure({ error: 'Failed to get attachments esarch result' }));
	}
}

export default function* gallerySaga() {
	yield takeLatest(types.FETCH_ATTACHMENTS_REQUEST, fetchAttachmentsSaga);
	yield takeLatest(types.RESET_GALLERY_FILTERS, fetchAttachmentsSaga);

	yield takeLatest(types.SEARCH_ATTACHMENTS_REQUEST, searchAttachmentsSaga);
}
